import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from '../components/ui/use-toast';
import {
  Search,
  Plus,
  Edit,
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Filter,
  Eye,
  Settings,
  MoreHorizontal,
  Upload,
  Download,
  RefreshCw,
  Building2,
  ShoppingCart,
  Box,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  UserPlus
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { SupplierProductsAPI } from '../lib/api';
import { SupplierProduct, SupplierProductCreateRequest, SupplierProductUpdateRequest } from '../lib/types';

// Components
import { StatusBadge } from '../components/ui/status-badge';
import SupplierProductCreateModal from '../components/supplier-products/SupplierProductCreateModal';
import SupplierProductEditModal from '../components/supplier-products/SupplierProductEditModal';
import SupplierProductViewModal from '../components/supplier-products/SupplierProductViewModal';
import StockAdjustmentModal from '../components/supplier-products/StockAdjustmentModal';
import ThresholdUpdateModal from '../components/supplier-products/ThresholdUpdateModal';
import AssignSupplierModal from '../components/supplier-products/AssignSupplierModal';

export default function SupplierProductsPage() {
  // State management
  const [products, setProducts] = useState<SupplierProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'price' | 'name'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const pageSize = 10;

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [isThresholdModalOpen, setIsThresholdModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAssignSupplierModalOpen, setIsAssignSupplierModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<SupplierProduct | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Ref to cache product data for modal display during close animation
  const productForModalRef = useRef<SupplierProduct | null>(null);

  // Sync ref with selectedProduct when it changes (but not when it becomes null)
  useEffect(() => {
    if (selectedProduct) {
      productForModalRef.current = selectedProduct;
    }
  }, [selectedProduct]);

  // Statistics
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    lowStockProducts: 0,
    outOfStockProducts: 0
  });

  // Available options for filters
  const [suppliers, setSuppliers] = useState<Array<{ _id: string; businessName: string }>>([]);
  const [categories, setCategories] = useState<Array<{ _id: string; name: string }>>([]);

  // Fetch products with filters
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
        supplier: selectedSupplier || undefined,
        category: selectedCategory || undefined,
        isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        lowStock: stockFilter === 'low' ? true : undefined,
        outOfStock: stockFilter === 'out' ? true : undefined,
        sortBy,
        sortOrder
      };

      const response = await SupplierProductsAPI.getAll(params);

      if (response.success) {
        setProducts(response.data.products);
        setTotalPages(response.data.pagination.pages);
        setTotalProducts(response.data.pagination.total);
        setStats(response.data.summary);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to fetch supplier products",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "Failed to fetch supplier products. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchQuery, selectedSupplier, selectedCategory, statusFilter, stockFilter, sortBy, sortOrder]);

  // Load filter options
  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      // Load suppliers and categories for filters
      const [suppliersResponse, categoriesResponse] = await Promise.all([
        SupplierProductsAPI.getAvailableSuppliers({ limit: 100 }),
        SupplierProductsAPI.getAvailableCategories({ limit: 100 })
      ]);

      if (suppliersResponse.success) {
        setSuppliers(suppliersResponse.data.suppliers);
      }

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data.categories);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  // Handle stock adjustment
  const handleStockAdjustment = async (productId: string, type: 'add' | 'remove' | 'set', quantity: number, notes?: string) => {
    try {
      const response = await SupplierProductsAPI.updateStock(productId, {
        quantity,
        transactionType: type === 'set' ? 'adjust' : type,
        notes
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Stock updated successfully",
          variant: "default"
        });

        // Refresh products
        fetchProducts();
        setIsStockModalOpen(false);
        setSelectedProduct(null);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update stock",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      toast({
        title: "Error",
        description: "Failed to update stock. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle threshold update
  const handleThresholdUpdate = async (productId: string, lowStockThreshold: number, reorderPoint: number) => {
    try {
      const response = await SupplierProductsAPI.updateThresholds(productId, {
        lowStockThreshold,
        reorderPoint
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Thresholds updated successfully",
          variant: "default"
        });
        fetchProducts();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update thresholds",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error updating thresholds:', error);
      toast({
        title: "Error",
        description: "Failed to update thresholds. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle product delete
  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    setIsDeleting(true);
    try {
      const response = await SupplierProductsAPI.delete(selectedProduct._id);

      if (response.success) {
        toast({
          title: "Success",
          description: "Product deleted successfully",
          variant: "default"
        });

        // Refresh products
        fetchProducts();
        setIsDeleteModalOpen(false);
        setSelectedProduct(null);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to delete product",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Error",
        description: "Failed to delete product. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle product toggle active
  const handleToggleActive = async (productId: string, currentStatus: boolean) => {
    try {
      const response = await SupplierProductsAPI.toggleActive(productId, {
        reason: `Product ${currentStatus ? 'deactivated' : 'activated'} by admin`
      });

      if (response.success) {
        toast({
          title: "Success",
          description: `Product ${currentStatus ? 'deactivated' : 'activated'} successfully`,
          variant: "default"
        });

        // Update local state
        setProducts(prev => prev.map(p =>
          p._id === productId ? { ...p, isActive: !currentStatus } : p
        ));
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update product status",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error toggling product status:', error);
      toast({
        title: "Error",
        description: "Failed to update product status. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedSupplier('');
    setSelectedCategory('');
    setStatusFilter('');
    setStockFilter('');
    setCurrentPage(1);
  };

  // Get stock status badge
  const getStockStatusBadge = (inventory: SupplierProduct['inventory']) => {
    if (inventory.isOutOfStock) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    }
    if (inventory.isLowStock) {
      return <Badge variant="secondary">Low Stock</Badge>;
    }
    return <Badge variant="default">In Stock</Badge>;
  };

  // Get supplier verification badge
  const getVerificationBadge = (status: string) => {
    return <StatusBadge status={status} label={status ? undefined : 'Unknown'} />;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Supplier Products</h1>
        <Button onClick={() => setIsCreateModalOpen(true)} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" />
          Create Product
        </Button>
      </div>

      {/* Compact Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Products</p>
                <p className="text-lg font-bold">{stats.totalProducts}</p>
                <p className="text-xs text-green-600">{stats.activeProducts} Active</p>
              </div>
              <Package className="h-4 w-4 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Stock Issues</p>
                <p className="text-lg font-bold">{stats.lowStockProducts + stats.outOfStockProducts}</p>
                <p className="text-xs text-orange-600">{stats.lowStockProducts} Low, {stats.outOfStockProducts} Out</p>
              </div>
              <AlertTriangle className="h-4 w-4 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Suppliers</p>
                <p className="text-lg font-bold">{suppliers.length}</p>
              </div>
              <Building2 className="h-4 w-4 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Categories</p>
                <p className="text-lg font-bold">{categories.length}</p>
              </div>
              <Box className="h-4 w-4 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {/* Search */}
            <div className="relative xl:col-span-2">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search products, suppliers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Supplier Filter */}
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">All Suppliers</option>
              {suppliers.map(supplier => (
                <option key={supplier._id} value={supplier._id}>
                  {supplier.businessName}
                </option>
              ))}
            </select>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* Stock Filter */}
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">All Stock</option>
              <option value="low">Low Stock</option>
              <option value="out">Out of Stock</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Products ({totalProducts})</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchProducts}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table className="min-w-[1000px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Loading products...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (products && products.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="text-muted-foreground">
                        <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No products found</p>
                        <p className="text-sm">Try adjusting your filters or create a new product</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product._id}>
                      <TableCell>
                        <div className="flex items-start gap-3">
                          {product.product.images.length > 0 && (
                            <img
                              src={product.product.images.find(img => img.isDefault)?.url || product.product.images[0]?.url}
                              alt={product.product.name}
                              className="w-12 h-12 rounded-md object-cover"
                            />
                          )}
                          <div>
                            <p className="font-medium">{product.product.name}</p>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <p className="text-sm text-muted-foreground cursor-help truncate max-w-[200px]">
                                    {product.product.description?.substring(0, 50)}
                                    {product.product.description && product.product.description.length > 50 ? '...' : ''}
                                  </p>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{product.product.description}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {product.product.unit}
                              </Badge>
                              {product.product.hsnCode && (
                                <Badge variant="outline" className="text-xs">
                                  HSN: {product.product.hsnCode}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div>
                          {product.supplier ? (
                            <>
                              <p className="font-medium">{product.supplier.businessName}</p>
                              <p className="text-sm text-muted-foreground">{product.supplier.ownerName}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {getVerificationBadge(product.supplier.verificationStatus)}
                                {product.supplier.rating && (
                                  <Badge variant="outline" className="text-xs">
                                    ⭐ {product.supplier.rating.toFixed(1)}
                                  </Badge>
                                )}
                              </div>
                            </>
                          ) : (
                            <p className="text-sm text-muted-foreground">Supplier information unavailable</p>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge variant="outline">{product.product.category.name}</Badge>
                      </TableCell>

                      <TableCell>
                        <div>
                          <p className="font-medium">₹{(product.price || 0).toLocaleString()}</p>
                          {product.product.basePrice && product.product.basePrice !== product.price && (
                            <p className="text-sm text-muted-foreground">
                              Base: ₹{product.product.basePrice.toLocaleString()}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Min: {product.minOrderQuantity} | Max: {product.maxQuantity}
                          </p>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {getStockStatusBadge(product.inventory)}
                          </div>
                          <p className="text-sm">
                            Available: {product.inventory.availableStock}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Total: {product.inventory.currentStock} | Reserved: {product.inventory.reservedStock}
                          </p>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={product.isActive}
                            onCheckedChange={() => handleToggleActive(product._id, product.isActive)}
                          />
                          <span className="text-sm">
                            {product.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedProduct(product);
                              setIsViewModalOpen(true);
                            }}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedProduct(product);
                              setIsEditModalOpen(true);
                            }}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Product
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedProduct(product);
                              setIsStockModalOpen(true);
                            }}>
                              <Package className="h-4 w-4 mr-2" />
                              Adjust Stock
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedProduct(product);
                              setIsThresholdModalOpen(true);
                            }}>
                              <Settings className="h-4 w-4 mr-2" />
                              Update Thresholds
                            </DropdownMenuItem>
                            {/* Show Assign Supplier option only for products without a supplier */}
                            {!product.supplier && (
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedProduct(product);
                                  setIsAssignSupplierModalOpen(true);
                                }}
                                className="text-blue-600 focus:text-blue-600"
                              >
                                <UserPlus className="h-4 w-4 mr-2" />
                                Assign Supplier
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedProduct(product);
                                setIsDeleteModalOpen(true);
                              }}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Product
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalProducts)} of {totalProducts} products
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <SupplierProductCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          fetchProducts();
        }}
      />

      {selectedProduct && (
        <SupplierProductEditModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            // Delay clearing selectedProduct to allow dialog to animate out
            setTimeout(() => setSelectedProduct(null), 300);
          }}
          product={selectedProduct}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setTimeout(() => setSelectedProduct(null), 300);
            fetchProducts();
          }}
        />
      )}

      {selectedProduct && (
        <SupplierProductViewModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setTimeout(() => setSelectedProduct(null), 300);
          }}
          product={selectedProduct}
        />
      )}

      {selectedProduct && (
        <StockAdjustmentModal
          isOpen={isStockModalOpen}
          onClose={() => {
            setIsStockModalOpen(false);
            setTimeout(() => setSelectedProduct(null), 300);
          }}
          product={selectedProduct}
          onAdjust={handleStockAdjustment}
        />
      )}

      {selectedProduct && (
        <ThresholdUpdateModal
          isOpen={isThresholdModalOpen}
          onClose={() => {
            setIsThresholdModalOpen(false);
            setTimeout(() => setSelectedProduct(null), 300);
          }}
          product={selectedProduct}
          onSuccess={handleThresholdUpdate}
        />
      )}

      {selectedProduct && (
        <AssignSupplierModal
          isOpen={isAssignSupplierModalOpen}
          onClose={() => {
            setIsAssignSupplierModalOpen(false);
            setTimeout(() => setSelectedProduct(null), 300);
          }}
          product={selectedProduct}
          onSuccess={() => {
            setIsAssignSupplierModalOpen(false);
            setTimeout(() => setSelectedProduct(null), 300);
            fetchProducts();
          }}
        />
      )}

      <Dialog open={isDeleteModalOpen} onOpenChange={(open) => {
        setIsDeleteModalOpen(open);
        if (!open) {
          setTimeout(() => setSelectedProduct(null), 300);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Delete Product
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{productForModalRef.current?.product?.name}" from {productForModalRef.current?.supplier?.businessName}?
              This action cannot be undone and will remove all related data including inventory and transaction history.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-800">This will permanently delete:</p>
                <ul className="mt-2 text-red-700 space-y-1">
                  <li>• The supplier product relationship</li>
                  <li>• All inventory records ({productForModalRef.current?.inventory?.currentStock ?? 0} units)</li>
                  <li>• All transaction history</li>
                  <li>• All price history records</li>
                </ul>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteProduct}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Product
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 